// ABOUTME: Unified data service using provider abstraction
// ABOUTME: Application-level database API that works with any backend provider

import { getProvider } from '@/providers'
import type { QueryOptions, DatabaseResult, RealtimeCallback } from '@/providers'
import type { WardrobeItem, Outfit, ChallengeProgress, ModuleCompletion } from '@/types/models'

/**
 * Data service
 *
 * Provides high-level data operations using the configured provider
 */
export class DataService {
  // ==================== Wardrobe Items ====================

  async getWardrobeItems(userId: string): Promise<WardrobeItem[]> {
    const provider = getProvider()
    return provider.database.query<WardrobeItem>('wardrobe_items', {
      filters: [{ field: 'userId', operator: 'eq', value: userId }],
      orderBy: { field: 'dateAdded', ascending: false },
    })
  }

  async getWardrobeItem(id: string): Promise<WardrobeItem | null> {
    const provider = getProvider()
    return provider.database.get<WardrobeItem>('wardrobe_items', id)
  }

  async createWardrobeItem(item: Omit<WardrobeItem, 'id'>): Promise<DatabaseResult<WardrobeItem>> {
    const provider = getProvider()
    return provider.database.insert<WardrobeItem>('wardrobe_items', item)
  }

  async updateWardrobeItem(id: string, item: Partial<WardrobeItem>): Promise<DatabaseResult<WardrobeItem>> {
    const provider = getProvider()
    return provider.database.update<WardrobeItem>('wardrobe_items', id, item)
  }

  async deleteWardrobeItem(id: string): Promise<{ error?: Error }> {
    const provider = getProvider()
    return provider.database.delete('wardrobe_items', id)
  }

  subscribeToWardrobeItems(userId: string, callback: RealtimeCallback<WardrobeItem>): () => void {
    const provider = getProvider()
    return provider.database.subscribe<WardrobeItem>(
      'wardrobe_items',
      callback,
      [{ field: 'userId', operator: 'eq', value: userId }]
    )
  }

  // ==================== Outfits ====================

  async getOutfits(userId: string): Promise<Outfit[]> {
    const provider = getProvider()
    return provider.database.query<Outfit>('outfits', {
      filters: [{ field: 'userId', operator: 'eq', value: userId }],
      orderBy: { field: 'createdAt', ascending: false },
    })
  }

  async getOutfit(id: string): Promise<Outfit | null> {
    const provider = getProvider()
    return provider.database.get<Outfit>('outfits', id)
  }

  async createOutfit(outfit: Omit<Outfit, 'id'>): Promise<DatabaseResult<Outfit>> {
    const provider = getProvider()
    return provider.database.insert<Outfit>('outfits', outfit)
  }

  async updateOutfit(id: string, outfit: Partial<Outfit>): Promise<DatabaseResult<Outfit>> {
    const provider = getProvider()
    return provider.database.update<Outfit>('outfits', id, outfit)
  }

  async deleteOutfit(id: string): Promise<{ error?: Error }> {
    const provider = getProvider()
    return provider.database.delete('outfits', id)
  }

  // ==================== Challenge Progress ====================

  async getChallengeProgress(userId: string): Promise<ChallengeProgress[]> {
    const provider = getProvider()
    return provider.database.query<ChallengeProgress>('challenge_progress', {
      filters: [{ field: 'userId', operator: 'eq', value: userId }],
    })
  }

  async getChallengeProgressById(challengeId: string, userId: string): Promise<ChallengeProgress | null> {
    const provider = getProvider()
    const results = await provider.database.query<ChallengeProgress>('challenge_progress', {
      filters: [
        { field: 'challengeId', operator: 'eq', value: challengeId },
        { field: 'userId', operator: 'eq', value: userId },
      ],
      limit: 1,
    })
    return results[0] || null
  }

  async saveChallengeProgress(progress: ChallengeProgress): Promise<DatabaseResult<ChallengeProgress>> {
    const provider = getProvider()

    // Check if progress already exists
    const existing = await this.getChallengeProgressById(progress.challengeId, progress.userId)

    if (existing?.id) {
      return provider.database.update<ChallengeProgress>('challenge_progress', existing.id, progress)
    } else {
      return provider.database.insert<ChallengeProgress>('challenge_progress', progress)
    }
  }

  subscribeToChallengeProgress(userId: string, callback: RealtimeCallback<ChallengeProgress>): () => void {
    const provider = getProvider()
    return provider.database.subscribe<ChallengeProgress>(
      'challenge_progress',
      callback,
      [{ field: 'userId', operator: 'eq', value: userId }]
    )
  }

  // ==================== Module Completions ====================

  async getModuleCompletions(userId: string): Promise<ModuleCompletion[]> {
    const provider = getProvider()
    return provider.database.query<ModuleCompletion>('module_completions', {
      filters: [{ field: 'userId', operator: 'eq', value: userId }],
    })
  }

  async getModuleCompletion(moduleId: string, userId: string): Promise<ModuleCompletion | null> {
    const provider = getProvider()
    const results = await provider.database.query<ModuleCompletion>('module_completions', {
      filters: [
        { field: 'moduleId', operator: 'eq', value: moduleId },
        { field: 'userId', operator: 'eq', value: userId },
      ],
      limit: 1,
    })
    return results[0] || null
  }

  async saveModuleCompletion(completion: ModuleCompletion): Promise<DatabaseResult<ModuleCompletion>> {
    const provider = getProvider()

    // Check if completion already exists
    const existing = await this.getModuleCompletion(completion.moduleId, completion.userId)

    if (existing?.id) {
      return provider.database.update<ModuleCompletion>('module_completions', existing.id, completion)
    } else {
      return provider.database.insert<ModuleCompletion>('module_completions', completion)
    }
  }

  subscribeToModuleCompletions(userId: string, callback: RealtimeCallback<ModuleCompletion>): () => void {
    const provider = getProvider()
    return provider.database.subscribe<ModuleCompletion>(
      'module_completions',
      callback,
      [{ field: 'userId', operator: 'eq', value: userId }]
    )
  }

  // ==================== User Profile ====================

  // biome-ignore lint/suspicious/noExplicitAny: dynamic user profile data
  async getUserProfile(userId: string): Promise<any | null> {
    const provider = getProvider()
    return provider.database.get('profiles', userId)
  }

  // biome-ignore lint/suspicious/noExplicitAny: dynamic user profile data
  async updateUserProfile(userId: string, profile: any): Promise<DatabaseResult<any>> {
    const provider = getProvider()
    return provider.database.update('profiles', userId, profile)
  }

  // biome-ignore lint/suspicious/noExplicitAny: dynamic wear log data
  async createWearLog(data: { itemId: string; outfitId?: string; wornAt?: string }): Promise<DatabaseResult<any>> {
    const provider = getProvider()
    return provider.database.insert('wear_logs', data)
  }

  // ==================== Generic Operations ====================

  async query<T>(table: string, options?: QueryOptions): Promise<T[]> {
    const provider = getProvider()
    return provider.database.query<T>(table, options)
  }

  async get<T>(table: string, id: string): Promise<T | null> {
    const provider = getProvider()
    return provider.database.get<T>(table, id)
  }

  async insert<T>(table: string, data: Omit<T, 'id' | 'createdAt' | 'updatedAt'>): Promise<DatabaseResult<T>> {
    const provider = getProvider()
    return provider.database.insert<T>(table, data)
  }

  async update<T>(table: string, id: string, data: Partial<T>): Promise<DatabaseResult<T>> {
    const provider = getProvider()
    return provider.database.update<T>(table, id, data)
  }

  async delete(table: string, id: string): Promise<{ error?: Error }> {
    const provider = getProvider()
    return provider.database.delete(table, id)
  }
}

// Singleton instance
let dataServiceInstance: DataService | null = null

/**
 * Get the data service instance
 */
export function getDataService(): DataService {
  if (!dataServiceInstance) {
    dataServiceInstance = new DataService()
  }
  return dataServiceInstance
}
